import type { ModelAnimation, ModelSoundFacts } from '@vrlearning/lesson-schema';

/**
 * When a model's sounds play (ADR-043; spec "Sound rules"): pure, so the
 * timing is tested apart from Web Audio and three.js.
 *
 * - `step` takes at the clip's foot contacts, on `lopen` and `rennen`;
 * - a `call` at `at` seconds into its clip (`brullen`, `bijten`, `eten`…);
 * - the `loop` breath on `staan`, every 4–8 s;
 * - one model's calls at least 1.5 s apart, every take at a pitch of
 *   0.92–1.05, the takes of a role in turn.
 */

export const MODEL_CUES = {
  /** One model's calls never closer together than this. */
  callSpacing: 1.5,
  /** Each take a little higher or lower: never the same sound twice. */
  pitch: [0.92, 1.05],
  /** The breath on `staan`, every so many seconds. */
  breath: [4, 8],
} as const;

/** A clip's position as a share of it, 0–1, before its first frame: everything at 0 still to come. */
export const CLIP_START = -1e-9;

/**
 * The contacts (fractions 0–1 of a clip) passed going from `before` to `now`:
 * those after `before` up to and including `now`, and across the clip's end
 * when it came round again (`now` below `before`).
 */
export function crossed(before: number, now: number, marks: readonly number[]): number[] {
  if (now >= before) return marks.filter((mark) => before < mark && mark <= now);
  return marks.filter((mark) => mark > before || mark <= now);
}

/** Where a call `at` seconds into a clip of `seconds` falls, as a share of it. */
export function callMark(at: number, seconds: number): number {
  return seconds > 0 ? Math.min(1, Math.max(0, at / seconds)) : 0;
}

/** A pitch for one take, from a number 0–1 (Math.random in the engine). */
export function pitchOf(random: number): number {
  const [low, high] = MODEL_CUES.pitch;
  return low + (high - low) * Math.min(1, Math.max(0, random));
}

/** Seconds to the next breath, from a number 0–1. */
export function breathIn(random: number): number {
  const [low, high] = MODEL_CUES.breath;
  return low + (high - low) * Math.min(1, Math.max(0, random));
}

/** A model's sound of `role` on `animation`, if the library has one. */
export function soundOf(
  sounds: readonly ModelSoundFacts[],
  animation: ModelAnimation,
  role: ModelSoundFacts['role'],
): ModelSoundFacts | undefined {
  return sounds.find((sound) => sound.animation === animation && sound.role === role);
}

/**
 * One model's ear for its own sounds: the takes in turn, its calls spaced,
 * the breath's timer. `now` is seconds of the lesson, standing still while
 * the teacher pauses.
 */
export class ModelCues {
  readonly #turns = new Map<ModelSoundFacts, number>();
  #lastCall = -Infinity;
  #breath: number;

  constructor(readonly random: () => number = Math.random) {
    this.#breath = breathIn(this.random());
  }

  /** The next take of a role, in turn. */
  take(sound: ModelSoundFacts): string | undefined {
    if (sound.media.length === 0) return undefined;
    const turn = this.#turns.get(sound) ?? 0;
    this.#turns.set(sound, turn + 1);
    return sound.media[turn % sound.media.length];
  }

  /** Whether a call may sound now: 1.5 s after the last one. Counts it when it may. */
  call(now: number): boolean {
    if (now - this.#lastCall < MODEL_CUES.callSpacing - 1e-9) return false;
    this.#lastCall = now;
    return true;
  }

  /** Breathing on `staan`: true when a breath is due, `dt` seconds on. */
  breathe(dt: number): boolean {
    this.#breath -= dt;
    if (this.#breath > 0) return false;
    this.#breath = breathIn(this.random());
    return true;
  }

  /** A new idle: the next breath a full 4–8 s away. */
  restartBreath(): void {
    this.#breath = breathIn(this.random());
  }

  pitch(): number {
    return pitchOf(this.random());
  }
}
